import type { Control } from "react-hook-form";
import { Grid } from "@mui/material";

import {
  FormSelectField,
  FormStateField,
  FormTextField,
  FormZipcodeField,
} from "@covertree/ui";
import type { OptionType } from "@covertree/types";
import { AdditionalInterestType } from "@covertree/new-business-graphql";

import {
  AdditionalInterestFormValues,
  AdditionalInterestNameField,
} from "./lenderAutofill";

interface SharedProps {
  readonly control: Control<AdditionalInterestFormValues>;
  readonly disabled?: boolean;
  readonly fontSize?: number;
  readonly labelFontSize?: number;
}

interface IdentityFieldsProps extends SharedProps {
  readonly typeOptions: readonly OptionType<AdditionalInterestType>[];
  readonly isTenantPolicy?: boolean;
  /** Locks the type while the rest of the form stays editable. */
  readonly typeDisabled?: boolean;
}

/**
 * Name and type, as grid items. A tenant has no lender to look up, so the name
 * is free text there instead of the lender autocomplete.
 */
export const AdditionalInterestIdentityFields = ({
  control,
  typeOptions,
  isTenantPolicy,
  typeDisabled,
  disabled,
  fontSize,
  labelFontSize,
}: IdentityFieldsProps) => (
  <>
    <Grid item md={6} xs={12}>
      {isTenantPolicy ? (
        <FormTextField
          id="name"
          name="name"
          label="Name"
          control={control}
          disabled={disabled}
          fontSize={fontSize}
          labelFontSize={labelFontSize}
        />
      ) : (
        <AdditionalInterestNameField
          control={control}
          disabled={disabled}
          fontSize={fontSize}
          labelFontSize={labelFontSize}
        />
      )}
    </Grid>
    <Grid item md={6} xs={12}>
      <FormSelectField
        id="type"
        name="type"
        label="Type"
        placeholder="Select type"
        control={control}
        options={typeOptions}
        disabled={disabled || typeDisabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
  </>
);

/** Address and contact fields, as grid items. */
export const AdditionalInterestAddressFields = ({
  control,
  disabled,
  fontSize,
  labelFontSize,
}: SharedProps) => (
  <>
    <Grid item xs={12}>
      <FormTextField
        id="street-address"
        name="streetAddress"
        label="Street Address"
        control={control}
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
    <Grid item md={6} xs={12}>
      <FormTextField
        id="lotUnit"
        name="lotUnit"
        label="Unit / Lot #"
        control={control}
        optional
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
    <Grid item md={6} xs={12}>
      <FormTextField
        id="city"
        name="city"
        label="City"
        control={control}
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
    <Grid item md={6} xs={12}>
      <FormStateField
        id="state"
        name="state"
        label="State"
        control={control}
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
    <Grid item md={6} xs={12}>
      <FormZipcodeField
        id="zipCode"
        name="zipCode"
        label="Zip Code"
        control={control}
        extendedZipcode
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
    <Grid item xs={12}>
      <FormTextField
        id="email"
        name="email"
        label="Email"
        control={control}
        type="email"
        tooltip="This email is only used for identification purposes. No communication will be sent."
        optional
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
    <Grid item xs={12}>
      <FormTextField
        id="account-number"
        name="accountNumber"
        label="Loan / account #"
        control={control}
        optional
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />
    </Grid>
  </>
);
